/****************************************************
 * Module Name:  shapeMapping.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import ShapeMappingProxy from "../../utility/proxy/nivodautility/shapeMappingProxy";
import AddShapeMappingModal from "./shapeMappingModal";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import PageTitle from "../common/pageTitle";

export class ShapeMapping extends Component {
    constructor(props) {
        super(props);

        this.state = {
            shapeMappings: [],
            showAddShapeMappingModal: false,
            accesDenied: false,
            editOperation: false,
            sourceData: {},
            errorSnackbar: false,
            loadingButton: false,
            showShapeMappingLoadSpinner: true,
            local_Permission: Constants.USER_PERMISSION.filter(
                (iPermission) =>
                    iPermission.permissionid === Constants.PERMISSIONS.ANALYSIS_NIVODA_SHAPE_MAPPING
            ),
            successSnackbar: false,
            successSnackbarMessage: "",
            filterValues: {}
        };

        this.checkPermission();
    }

    componentDidMount() {
        PageTitle("ANALYSIS_NIVODA_SHAPE_MAPPING");
    }

    checkPermission = () => {
        let allowed = Utility.isReadAllowed(this.state.local_Permission)
        if (!allowed) {
            this.setState({
                accesDenied: true
            })
        } else {
            this.fetchShapeMappingData(false);
        }
    }

    fetchShapeMappingData = (bAnimateSpinner) => {
        bAnimateSpinner && this.setState({ showShapeMappingLoadSpinner: true });
        ShapeMappingProxy.GetAllShapeMapping((responseData) => {
            if (responseData) {
                const uniqueFilterValues = {
                    shape: Utility.getDistinctOptions(responseData, "shape"),
                };
                this.setState({ shapeMappings: responseData, showShapeMappingLoadSpinner: false, filterValues: uniqueFilterValues })
            } else {
                this.setState({ errorSnackbar: true, showShapeMappingLoadSpinner: false })
            }
        }
        );
    };

    options = {
        sizePerPage: 50,
    };

    initialColumns = () => [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "detailedShape",
            headerName: "DETAILED SHAPE",
            headerAlign: "center",
            align: "center",
            flex: 1,
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: this.state.filterValues ? this.state.filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            this.setState({
                                showAddShapeMappingModal: true,
                                editOperation: true,
                                sourceData: params.row,
                            });
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    hideAddShapeMappingModal = () => {
        this.setState({
            showAddShapeMappingModal: false,
        });
    };

    handleValidation = (shapeMappingData) => {
        //TODO do validation if needed here
        return true;
    };

    addShapeMapping = (shapeMappingData) => {
        this.setState({ loadingButton: true });
        if (this.state.editOperation) {
            //Updating ShapeMapping, and once ShapeMapping is updated on call refresh the list of current ShapeMappings
            ShapeMappingProxy.UpdateShapeMapping(shapeMappingData, (response) => {
                if (response) {
                    this.fetchShapeMappingData(true);
                    this.setState({ loadingButton: false, successSnackbar: true, successSnackbarMessage: "Shape Mapping Updated Successfully" });
                    //Hidding size modal
                    this.hideAddShapeMappingModal();
                } else {
                    this.setState({ errorSnackbar: true });
                    this.setState({ loadingButton: false });
                }
            });
        } else {
            //Creating new ShapeMapping, and once ShapeMapping is created on call refresh the list of current ShapeMappings
            ShapeMappingProxy.AddShapeMapping(shapeMappingData, (response) => {
                if (response) {
                    this.fetchShapeMappingData(true);
                    this.setState({ loadingButton: false, successSnackbar: true, successSnackbarMessage: "Shape Mapping Added Successfully" });
                    //Hidding size modal
                    this.hideAddShapeMappingModal();
                } else {
                    this.setState({ errorSnackbar: true });
                    this.setState({ loadingButton: false });
                }
            });
        }
    };

    render() {
        const CustomToolbar = () => {
            return (
                <GridToolbarContainer
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        marginHorizontal: "5px",
                        "&.MuiDataGrid-toolbarContainer": {
                            padding: "0px 4px !important",
                        },
                    }}
                >
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarExport />
                    <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                    {this.state.showShapeMappingLoadSpinner ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                color="primary"
                                fontSize="medium"
                                onClick={() => this.fetchShapeMappingData(true)}
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}

                    {Utility.isAddAllowed(this.state.local_Permission) ? (
                        <Tooltip title="Add Size">
                            <Button
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{ ml: "5px" }}
                                onClick={() =>
                                    this.setState({
                                        showAddShapeMappingModal: true,
                                        editOperation: false,
                                        sourceData: {},
                                    })
                                }
                                startIcon={<AddBoxOutlinedIcon size="large" />}
                            >
                                Shape Mapping
                            </Button>
                        </Tooltip>
                    ) : null}
                    <GridToolbarQuickFilter debounceMs={500} />
                </GridToolbarContainer>
            );
        }
        let allowed = Utility.isReadAllowed(this.state.local_Permission)
        if (!allowed) {
            return <NoPermission />
        }
        const rows = this.state.shapeMappings;
        const columns = Utility.isUpdateAllowed(this.state.local_Permission)
            ? this.initialColumns()
            : this.initialColumns().filter((col) => col.field !== "edit");

        return (
            <Box>
                <CommonSnackbar
                    show={this.state.errorSnackbar}
                    message={<Box>{Constants.APiFailErrorMessage}</Box>}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    handleClose={() => this.setState({ errorSnackbar: false })}
                    title="Failed"
                />

                <TabMenuSnackbar
                    show={this.state.successSnackbar}
                    message={this.state.successSnackbarMessage}
                    variant={"success"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={"Success"}
                    handleClose={() => this.setState({ successSnackbar: false })}
                />

                <br />
                {this.state.showAddShapeMappingModal ? (
                    <AddShapeMappingModal
                        Show={true}
                        handleClose={this.hideAddShapeMappingModal}
                        handleSave={this.addShapeMapping}
                        handleValidation={this.handleValidation}
                        editOperation={this.state.editOperation}
                        sourceData={this.state.sourceData}
                        loading={this.state.loadingButton}
                    />
                ) : (
                    <Box />
                )}
                <Box px={1}>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "85vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },
                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            loading={this.state.showShapeMappingLoadSpinner}
                            slots={{ toolbar: CustomToolbar }}
                            density="compact"
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => row.id}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns status and traderName, the other columns will remain visible
                                        id: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box >
        );
    }
}

export default ShapeMapping;
